/**
 * HoldingSetupWizard — REQUIRED first-login onboarding modal.
 *
 * Mounted at AppRouter level. Appears as a full-screen overlay (with
 * the dashboard chrome visible behind a backdrop) whenever the current
 * user is a `holding_admin` whose tenant has `setup_done = false`.
 *
 * Three minimum-required inputs (per v163 product spec):
 *   1. Holding / brand name
 *   2. Website URL
 *   3. Logo (URL or paste-and-preview)
 *
 * On submit:
 *   POST /api/tenants/me/setup-complete  →  flips `setup_done=true`
 *   on the tenant doc; the modal then unmounts and the holding_admin
 *   can use the dashboard normally.
 *
 * Why this is REQUIRED (not dismissible):
 *   The holding's brand_name + website are surfaced on the Trust Pack
 *   PDFs, on email templates, on the public Trust Center, and on the
 *   tenant's white-label login page. Letting a holding_admin defer the
 *   setup creates broken Trust Packs and unbranded email blasts that
 *   damage their own brand.
 */
import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Building2, Globe, ImagePlus, Loader2, CheckCircle2, Sparkles } from 'lucide-react';

/* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on the user-bound tenant load effect. */

export default function HoldingSetupWizard() {
  const { user, refresh } = useAuth();
  const [tenant, setTenant] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    brand_name: '',
    website: '',
    logo_url: '',
    primary_color: '#D4AF37',
  });

  // Fetch the current tenant on mount so we know whether to show the
  // wizard at all. Anyone but a holding_admin (or system_admin cloaked
  // as one) is exempt — system_admin pages get the maker-pill chrome
  // instead.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!user) { setLoaded(true); return; }
        // Only holding_admins need this wizard.
        if (user.role !== 'holding_admin') { setLoaded(true); return; }
        const { data } = await api.get('/tenants/me');
        if (cancelled) return;
        setTenant(data);
        setForm((f) => ({
          ...f,
          brand_name: data.brand_name || f.brand_name,
          website: data.website || f.website,
          logo_url: data.logo_url || f.logo_url,
          primary_color: data.primary_color || f.primary_color,
        }));
      } catch (e) {
        // Non-fatal — if /tenants/me fails we just don't show the wizard.
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  // Render decision:
  //  • not loaded yet — nothing (avoids flash)
  //  • not a holding_admin — nothing
  //  • tenant already has setup_done — nothing
  //  • tenant already has the three required fields populated (brand_name +
  //    website + logo_url) — nothing. This covers seeded / pre-provisioned
  //    tenants whose setup_done flag may not have been flipped, so the
  //    holding_admin isn't blocked by a redundant wizard.
  //  • everything else — full overlay
  if (!loaded || !user) return null;
  if (user.role !== 'holding_admin') return null;
  if (!tenant) return null;
  if (tenant.setup_done) return null;
  if (
    (tenant.brand_name || '').trim() &&
    (tenant.website || '').trim() &&
    (tenant.logo_url || '').trim()
  ) {
    return null;
  }

  const next = () => {
    if (step === 0 && !form.brand_name.trim()) {
      toast.error('Brand name is required.');
      return;
    }
    if (step === 1 && form.website && !/^https?:\/\//i.test(form.website)) {
      toast.error('Website must start with http:// or https://');
      return;
    }
    setStep((s) => s + 1);
  };

  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    setBusy(true);
    try {
      await api.post('/tenants/me/setup-complete', {
        brand_name: form.brand_name.trim(),
        website: form.website.trim() || null,
        logo_url: form.logo_url.trim() || null,
        primary_color: form.primary_color,
      });
      toast.success(`${form.brand_name} is now set up. Welcome.`);
      // Refresh the local user + tenant state so the wizard unmounts.
      if (refresh) await refresh();
      setTenant({ ...tenant, setup_done: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Could not complete setup.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/80 backdrop-blur-md flex items-center justify-center p-4"
      data-testid="holding-setup-wizard"
    >
      <div className="w-full max-w-lg rounded-xl border border-amber-500/30 bg-zinc-950 text-white shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 border-b border-white/5">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-amber-400 mb-1">
            <Sparkles size={11} className="inline -mt-0.5 mr-1" />
            Welcome · holding setup
          </div>
          <h2 className="text-xl font-bold">Let&apos;s set up your holding</h2>
          <p className="text-zinc-400 text-[13px] mt-1.5 leading-relaxed">
            Three quick things — your brand name, your website, and a logo — so your Trust Packs,
            invoices, emails, and login pages all carry your identity.
          </p>
          <div className="mt-4 flex items-center gap-1.5">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  i < step ? 'bg-emerald-500' : i === step ? 'bg-amber-500' : 'bg-zinc-700'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-6 space-y-4 min-h-[200px]">
          {step === 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-400 mb-2 flex items-center gap-1.5">
                <Building2 size={11} /> Step 1 of 3 · Your holding&apos;s name
              </div>
              <input
                autoFocus
                value={form.brand_name}
                onChange={(e) => setForm({ ...form, brand_name: e.target.value })}
                data-testid="wizard-brand-name"
                className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2.5 text-lg font-bold focus:outline-none focus:border-amber-500"
                placeholder="e.g. ACME Holdings"
                maxLength={80}
              />
              <p className="text-[10.5px] text-zinc-500 mt-2 leading-relaxed">
                This appears at the top of every Trust Pack, on email subject lines, on your
                white-label login page, and anywhere the platform shows &quot;the brand&quot;.
              </p>
            </div>
          )}

          {step === 1 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-400 mb-2 flex items-center gap-1.5">
                <Globe size={11} /> Step 2 of 3 · Your website
              </div>
              <input
                autoFocus
                type="url"
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
                data-testid="wizard-website"
                className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2.5 text-lg focus:outline-none focus:border-amber-500"
                placeholder="https://acme-holdings.com"
              />
              <p className="text-[10.5px] text-zinc-500 mt-2 leading-relaxed">
                Used as the &quot;Verified by&quot; link on Trust Packs and the canonical link
                in security email footers. Leave blank if you don&apos;t have a public site yet.
              </p>
            </div>
          )}

          {step === 2 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-400 mb-2 flex items-center gap-1.5">
                <ImagePlus size={11} /> Step 3 of 3 · Logo & accent color
              </div>
              <input
                value={form.logo_url}
                onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                data-testid="wizard-logo"
                className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2.5 text-sm focus:outline-none focus:border-amber-500"
                placeholder="https://… (PNG/SVG, square, transparent BG)"
              />
              {form.logo_url && (
                <div className="mt-3 flex items-center gap-3 p-3 rounded border border-zinc-700 bg-zinc-900/60">
                  <div className="h-12 w-12 rounded bg-zinc-800 flex items-center justify-center overflow-hidden">
                    <img
                      src={form.logo_url}
                      alt="Logo preview"
                      className="max-h-full max-w-full object-contain"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  </div>
                  <div className="text-[11px] text-zinc-400">
                    Preview · your logo will appear here in the platform sidebar and Trust Packs.
                  </div>
                </div>
              )}
              <div className="mt-4">
                <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-400 mb-1.5 block">
                  Primary color
                </span>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={form.primary_color}
                    onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                    className="w-12 h-10 rounded border border-zinc-700 bg-zinc-900 cursor-pointer"
                  />
                  <input
                    value={form.primary_color}
                    onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                    className="bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm font-mono"
                  />
                </div>
              </div>
              <p className="text-[10.5px] text-zinc-500 mt-3 leading-relaxed">
                Skipping the logo is fine — you can upload one later from{' '}
                <code className="text-[10px] font-mono px-1 py-0.5 rounded bg-zinc-800 text-amber-300">/dashboard/branding</code>.
                Color you pick here drives the whole platform theme.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/5 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={back}
            disabled={step === 0 || busy}
            className="px-3 py-1.5 text-[12px] text-zinc-400 hover:text-white disabled:opacity-40"
          >
            ← Back
          </button>

          {step < 2 ? (
            <button
              type="button"
              onClick={next}
              data-testid="wizard-next"
              className="px-4 py-2 rounded bg-amber-500 hover:bg-amber-400 text-black text-sm font-bold"
            >
              Next →
            </button>
          ) : (
            <button
              type="button"
              onClick={submit}
              disabled={busy}
              data-testid="wizard-finish"
              className="px-4 py-2 rounded bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-bold inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
              Finish setup
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
